import { useMemo, useRef, useState } from 'react'
import {
  Plus,
  FileText,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  Heart,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { usePatientRecords } from '../hooks/use-patient-records'
import { useDeleteRecord, useUpdateRecord } from '../hooks/use-patient-records'
import { useTags } from '../hooks/use-tags'
import { RecordCard } from './RecordCard'
import { RecordForm } from './RecordForm'
import { FamilySummaryReviewModal } from './FamilySummaryReviewModal'
import { useAuth } from '@/features/auth'
import { useToast } from '@/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { PatientRecordWithRelations } from '@repo/shared'

interface PatientRecordsTabProps {
  patientId: string
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message
  }
  return fallback
}

export function PatientRecordsTab({ patientId }: PatientRecordsTabProps) {
  const { currentOrganization } = useAuth()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingRecord, setEditingRecord] =
    useState<PatientRecordWithRelations | null>(null)
  const [deletingRecord, setDeletingRecord] =
    useState<PatientRecordWithRelations | null>(null)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [familySummaryRecordId, setFamilySummaryRecordId] = useState<
    string | null
  >(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const perPage = 20
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const canManageRecords =
    currentOrganization?.userRole === 'admin' ||
    currentOrganization?.userRole === 'professional'

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value)
      setPage(1)
    }, 400)
  }

  const { data, isLoading, error } = usePatientRecords(
    patientId,
    page,
    perPage,
    debouncedSearch || undefined,
  )
  const { data: allTags } = useTags()
  const deleteRecord = useDeleteRecord()
  const updateRecord = useUpdateRecord()
  const { toast } = useToast()

  const records = data?.data
  const totalCount = data?.count ?? 0
  const totalPages = Math.ceil(totalCount / perPage)

  const filteredRecords = useMemo(() => {
    const availableRecords = records ?? []
    if (selectedTagIds.length === 0) return availableRecords
    return availableRecords.filter((record) =>
      selectedTagIds.every((tagId) => record.tags?.some((t) => t.id === tagId)),
    )
  }, [records, selectedTagIds])

  const toggleTagFilter = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    )
  }

  const handleEdit = (record: PatientRecordWithRelations) => {
    setEditingRecord(record)
    setIsFormOpen(true)
  }

  const handleDelete = (record: PatientRecordWithRelations) => {
    setDeletingRecord(record)
  }

  const handleToggleVisibility = async (record: PatientRecordWithRelations) => {
    if (!canManageRecords) return
    const newVisibility = record.visibility === 'private' ? 'shared' : 'private'
    try {
      await updateRecord.mutateAsync({
        id: record.id,
        patient_id: patientId,
        visibility: newVisibility,
      })
      toast({
        title: 'Visibilidade alterada',
        description:
          newVisibility === 'shared'
            ? 'Registro compartilhado com a equipe.'
            : 'Registro agora e privado.',
      })
    } catch (error) {
      toast({
        title: 'Erro',
        description: getErrorMessage(
          error,
          'Nao foi possivel alterar a visibilidade.',
        ),
        variant: 'destructive',
      })
    }
  }

  const confirmDelete = async () => {
    if (!deletingRecord) return
    try {
      await deleteRecord.mutateAsync({
        id: deletingRecord.id,
        patient_id: patientId,
      })
      toast({
        title: 'Registro excluido',
        description: 'O registro foi removido com sucesso.',
      })
    } catch (error) {
      toast({
        title: 'Erro',
        description: getErrorMessage(
          error,
          'Nao foi possivel excluir o registro.',
        ),
        variant: 'destructive',
      })
    } finally {
      setDeletingRecord(null)
    }
  }

  const closeForm = () => {
    setIsFormOpen(false)
    setEditingRecord(null)
    setIsFormDirty(false)
  }

  const handleRequestClose = () => {
    if (isFormDirty) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const handleFormSuccess = () => {
    closeForm()
  }

  if (isLoading) {
    return <RecordsListSkeleton />
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-red-500">
          Erro ao carregar registros. Tente novamente.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with new record button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Registros de Evolucao
        </h3>
        {canManageRecords && (
          <Button
            size="sm"
            onClick={() => {
              setEditingRecord(null)
              setIsFormOpen(true)
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Novo Registro
          </Button>
        )}
      </div>

      {!canManageRecords && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Voce pode visualizar os registros, mas apenas profissionais e
          administradores podem criar ou alterar evolucoes.
        </p>
      )}

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar nos registros..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => {
              setSearchQuery('')
              setDebouncedSearch('')
              setPage(1)
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tag filter bar */}
      {allTags && allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 mr-1">Filtrar por tag:</span>
          {allTags.map((tag) => {
            const isActive = selectedTagIds.includes(tag.id)
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTagFilter(tag.id)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-teal-100 text-teal-800 ring-1 ring-teal-500'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tag.color && (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                )}
                {tag.name}
                {isActive && <X className="h-3 w-3" />}
              </button>
            )
          })}
          {selectedTagIds.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedTagIds([])}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Records list */}
      {filteredRecords.length === 0 ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-4">
            <FileText className="h-6 w-6 text-gray-400" />
          </div>
          {selectedTagIds.length > 0 ? (
            <>
              <p className="text-sm font-medium text-gray-600">
                Nenhum registro encontrado com as tags selecionadas
              </p>
              <button
                type="button"
                onClick={() => setSelectedTagIds([])}
                className="text-sm text-teal-600 hover:text-teal-700 underline mt-2"
              >
                Limpar filtros
              </button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-600">
                Nenhum registro de evolucao encontrado
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Comece documentando a evolucao do paciente.
              </p>
              <Button
                size="sm"
                className="mt-4"
                disabled={!canManageRecords}
                onClick={() => setIsFormOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Criar primeiro registro
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRecords.map((record) => (
            <div key={record.id} className="space-y-1.5">
              <RecordCard
                record={record}
                patientId={patientId}
                onEdit={canManageRecords ? handleEdit : undefined}
                onDelete={canManageRecords ? handleDelete : undefined}
                onToggleVisibility={
                  canManageRecords ? handleToggleVisibility : undefined
                }
              />
              {/* Family share status for records with visibility === 'family' */}
              {record.visibility === 'family' && canManageRecords && (
                <FamilyShareStatus
                  record={record}
                  onGenerateSummary={() => setFamilySummaryRecordId(record.id)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > perPage && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-gray-500">
            Mostrando {Math.min((page - 1) * perPage + 1, totalCount)}-
            {Math.min(page * perPage, totalCount)} de {totalCount} registros
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600 px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={isFormOpen}
        onOpenChange={(open) => {
          if (!open) handleRequestClose()
        }}
      >
        <DialogContent
          className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>
              {editingRecord ? 'Editar Registro' : 'Novo Registro de Evolucao'}
            </DialogTitle>
            <DialogDescription>
              Registre a evolucao do atendimento, incluindo observacoes, tags e
              anexos.
            </DialogDescription>
          </DialogHeader>
          <RecordForm
            patientId={patientId}
            record={editingRecord ?? undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleRequestClose}
            onDirtyChange={setIsFormDirty}
          />
        </DialogContent>
      </Dialog>

      {/* Discard confirmation dialog */}
      <AlertDialog
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar alteracoes?</AlertDialogTitle>
            <AlertDialogDescription>
              Voce tem conteudo nao salvo. Se fechar agora, as informacoes
              digitadas e os arquivos anexados serao perdidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction
              onClick={closeForm}
              className="bg-red-600 hover:bg-red-700"
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Family Summary Review Modal */}
      {familySummaryRecordId && (
        <FamilySummaryReviewModal
          open={!!familySummaryRecordId}
          onOpenChange={(open) => {
            if (!open) setFamilySummaryRecordId(null)
          }}
          recordId={familySummaryRecordId}
          patientId={patientId}
        />
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deletingRecord}
        onOpenChange={(open) => {
          if (!open) setDeletingRecord(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este registro? Esta acao nao pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function FamilyShareStatus({
  record,
  onGenerateSummary,
}: {
  record: PatientRecordWithRelations
  onGenerateSummary: () => void
}) {
  const status = (
    record as PatientRecordWithRelations & {
      family_share_status?: string | null
      family_share_sent_at?: string | null
    }
  ).family_share_status
  const sentAt = (
    record as PatientRecordWithRelations & {
      family_share_sent_at?: string | null
    }
  ).family_share_sent_at

  if (!status) {
    // No status yet — show generate button
    return (
      <div className="px-4 pb-2">
        <Button
          variant="outline"
          size="sm"
          className="text-teal-700 border-teal-300 hover:bg-teal-50"
          onClick={onGenerateSummary}
        >
          <Heart className="h-3.5 w-3.5 mr-1.5" />
          Gerar resumo para revisão
        </Button>
      </div>
    )
  }

  if (status === 'pending') {
    return (
      <div className="px-4 pb-2 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <Clock className="h-3 w-3" />
          Pendente
        </span>
        <Button
          variant="outline"
          size="sm"
          className="text-teal-700 border-teal-300 hover:bg-teal-50 h-7 text-xs"
          onClick={onGenerateSummary}
        >
          <FileText className="h-3 w-3 mr-1" />
          Revisar resumo
        </Button>
      </div>
    )
  }

  if (status === 'sent') {
    return (
      <div className="px-4 pb-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle2 className="h-3 w-3" />
          Enviado para familia
          {sentAt && (
            <span className="text-green-600 ml-1">
              {new Date(sentAt).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
        </span>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div className="px-4 pb-2 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3" />
          Falha no envio
        </span>
        <Button
          variant="outline"
          size="sm"
          className="text-red-700 border-red-300 hover:bg-red-50 h-7 text-xs"
          onClick={onGenerateSummary}
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Tentar novamente
        </Button>
      </div>
    )
  }

  return null
}

function RecordsListSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-9 w-32" />
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="card p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div>
              <Skeleton className="h-4 w-32 mb-1" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <Skeleton className="h-16 w-full" />
        </div>
      ))}
    </div>
  )
}
